import {
    SWITCH_TO_PATTERN,
    PLAY_WORK,
    LOAD_WORK,
    UNLOAD_WORK,
    CHANGE_TRANSPOSE,
    CHANGE_VOLUME,
    CHANGE_BPM,
    CHANGE_COLOR,
    ROLL_ON,
    ROLL_OFF,
} from './constants.js';

import Tone from 'tone';
import {
    setWorksLoaded,
    setWorkTranspose,
    setMixColorValue,
    setMixHasEdits,
    setWorkVolume,
} from 'actions/index.js';

// mixin for recording mixes in explore page.
// gets mixed in with MultiSequencerEngine

export default superclass => class extends superclass  {
    events = [];
    hasEdits = false;

    get isRecordingMix() {
        return this.state.explore.isRecordingMix;
    }

    get currentTransportTime() {
        let sixteenths = parseFloat(Tone.Transport.position.split(":")[2]);
        let remainder = sixteenths - Math.floor(sixteenths);
        return "0:0:" + (remainder + (this.trackStep / 2));
    }

    dispatch(action, scheduled=false) {
        let time = this.currentTransportTime; 
        let sequencerEngine =
            action.sequenceId && this.getSequencerEngine(action.sequenceId);

        switch (action.type) {
        case SWITCH_TO_PATTERN:
            this.switchToPattern(
                sequencerEngine,
                action.subSequenceId,
                action.patternNumber
            );
            break;
        case PLAY_WORK:
            this.playWork(action.work);
            break;
        case LOAD_WORK:
            this.loadWork(action.work);
            break;
        case UNLOAD_WORK:
            this.unloadWork(action.work);
            break;
        case CHANGE_TRANSPOSE:
            this.changeTranspose(sequencerEngine, action.transpose);
            break;
        case CHANGE_VOLUME:
            this.changeVolume(sequencerEngine, action.volume);
            break;
        case CHANGE_COLOR:
            this.changeColor(sequencerEngine, action.color);
            break;
        case CHANGE_BPM:
            this.changeBPM(action.bpm);
            break;
        }

        if (!scheduled && this.isRecordingMix) {
            action = {
                ... action,
                time: time
            };
            this.events.push(action);
            this.hasEdits = true;
        }
    }

    playWork(work) {
        this.play(work);

        if (this.isNothingPlaying()) {
            // then we must clear
            this.clearScheduledEvents();
        }
    }

    get worksLoaded() {
        return this.state.explore.worksLoaded;
    }

    async loadWork(work) {
        console.log('MultiMix.loadWork =', work);
        // first check if work is loaded already
        if (this.worksLoaded.some(
            w => w.newSequence === work.newSequence)) {
            let sequencerEngine = this.getSequencerEngine(work.newSequence);
            this.setWorkDefaults(sequencerEngine);
            return;
        }

        // if not then load it
        this.store.dispatch(
            setWorksLoaded([... this.worksLoaded, work]));

        await this.loadTrack(work.newSequence);
    }

    unloadWork(work) {
        // first check that we havent already unloaded
        if (!this.worksLoaded.some(
            w => w.newSequence === work.newSequence)) {
            return;
        }
        let worksLoaded = [... this.worksLoaded];
        let index = worksLoaded.indexOf(work);
        worksLoaded.splice(index, 1);
        this.store.dispatch(
            setWorksLoaded(worksLoaded));
        this.unloadTrack(work.newSequence);
    }

    switchToPattern(sequencerEngine, subSequenceId, patternNumber) {
        if (!sequencerEngine) {
            return;
        }
        sequencerEngine.switchToSubSequence(subSequenceId);
        sequencerEngine.switchToPattern(patternNumber);
    }

    changeColor(sequencerEngine, color) {
        if (!sequencerEngine) {
            return;
        }
        sequencerEngine.channelMixer.mixEffects.filter = color;
        this.store.dispatch(
            setMixColorValue(
                sequencerEngine.sequenceId,
                color));
    }

    changeTranspose(sequencerEngine, transpose) {
        if (!sequencerEngine) {
            return;
        }
        sequencerEngine.transpose = transpose;
        this.store.dispatch(
            setWorkTranspose(
                sequencerEngine.sequenceId,
                transpose));
    }

    changeVolume(sequencerEngine, volume) {
        if (!sequencerEngine) {
            return;
        }
        sequencerEngine.setMasterVolume(volume, 0.05);
        this.store.dispatch(
            setWorkVolume(
                sequencerEngine.sequenceId,
                volume));
    }

    changeBPM(bpm) {
        this.updateBpm(bpm);
    }

    setInitialValuesForMix() {
        if (this.initialState) {
            let {
                workVolumes,
                worksLoaded,
                workTransposes,
                mixColorValues,
                bpm
            } = this.initialState;

            if (bpm) {
                this.changeBPM(bpm);
            }
            
            for (let id in workVolumes) {
                let engine = this.getSequencerEngine(id);
                if (engine) {
                    this.changeVolume(
                        engine,
                        workVolumes[id]);
                }
            }
            for (let id in workTransposes) {
                let engine = this.getSequencerEngine(id);
                if (engine) {
                    this.changeTranspose(
                        engine,
                        workTransposes[id]);
                }
            }
            for (let id in worksLoaded) {
                this.loadWork(worksLoaded[id]);
            }
            for (let id in mixColorValues) {
                let engine = this.getSequencerEngine(id);
                if (engine) {
                    this.changeColor(
                        engine,
                        mixColorValues[id]);
                }
            }
        }
    }

    saveInitialValuesForMix() {
        let state = this.store.getState();
        this.initialState = {
            worksLoaded: [... state.explore.worksLoaded],
            workTransposes: {... state.explore.workTransposes},
            workVolumes: {... state.explore.workVolumes},
            bpm: state.user.bpm,
            mixColorValues: {... state.explore.mixColorValues}
        };
    }

    playMix(play) {
        if (play) {
            if (this.isRecordingMix) {
                this.saveInitialValuesForMix();
            } else {
                this.setInitialValuesForMix();
            }

            this.eventIds = this.events.map(
                event =>
                    Tone.Transport.schedule((time) => {
                        if (event.type === PLAY_WORK) {
                            time = 0;
                        }
                        setTimeout(() => {
                            this.dispatch(event, true);
                        }, time);
                    }, event.time));
            this.play();
        } else {
            this.clearScheduledEvents();
            this.stop();
        }

    }

    clearScheduledEvents() {
        if (this.eventIds) {
            this.eventIds.forEach(id => Tone.Transport.clear(id));
            this.eventIds = [];
        }
    }

    setWorkDefaults(work) {
        this.changeTranspose(work, 0);
        this.changeVolume(work, 0);
    }

    set hasEdits(edits) {
        this.store.dispatch(
            setMixHasEdits(edits));
    }

    unloadAll() {
        this.clearMix();
        this.stop();
        this.hasEdits = false;
        
        let worksLoaded = [... this.worksLoaded];
        for (let i in worksLoaded) {
            this.unloadWork(worksLoaded[i]);
        }
    }
}
